import { Link } from 'react-router-dom';
import { MapPin, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import PaymentBadges from '@/components/PaymentBadges';
import api from '@/lib/api';

export default function Footer() {
    const year = new Date().getFullYear();
    const [cfg, setCfg] = useState({
        phone: '07050657701',
        address: 'Shop B308, Sheriff Plaza, Aminu Kano Crescent, Wuse 2, Abuja',
        city: 'Abuja, Nigeria',
        returnSummary: 'Returns accepted within 7 days for unopened products. Contact us via WhatsApp.',
    });
    const [categories, setCategories] = useState([]);
    useEffect(() => {
        api.get('/settings').then(({ data }) => {
            setCfg({
                phone: data.phone_number || data.whatsapp_number || '07050657701',
                address: data.store_address || 'Shop B308, Sheriff Plaza, Aminu Kano Crescent, Wuse 2, Abuja',
                city: data.store_city || 'Abuja, Nigeria',
                returnSummary: data.return_policy_summary || 'Returns accepted within 7 days for unopened products. Contact us via WhatsApp.',
            });
        }).catch(() => {});
        // Pull the same taxonomy used by the header so footer links stay in lock-step
        api.get('/categories').then(({ data }) => setCategories(data || [])).catch(() => {});
    }, []);
    return (
        <footer className="bg-gray-900 text-gray-300" data-testid="main-footer">
            {/* ───── Desktop / tablet full footer ───── */}
            <div className="hidden md:block max-w-7xl mx-auto px-4 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="md:col-span-1">
                        <h3 className="font-heading text-xl font-bold text-white mb-3">Beauty<span className="text-pink-500">By</span>Chancy</h3>
                        <p className="text-sm font-body text-gray-400 mb-4">Glow Beautifully, Naturally. Premium skincare and cosmetics brand in Abuja.</p>
                        <div className="mt-4">
                            <PaymentBadges className="[&_span]:text-gray-400 [&_div]:border-gray-700 [&_div]:bg-gray-800" />
                        </div>
                    </div>
                    <div>
                        <h4 className="font-heading text-sm font-semibold text-white uppercase tracking-wider mb-4">Shop by Category</h4>
                        <ul className="space-y-2 font-body text-sm" data-testid="footer-categories">
                            <li><Link to="/shop" className="hover:text-pink-400 transition-colors">Shop All</Link></li>
                            {categories.map(c => (
                                <li key={c.id}>
                                    <Link to={`/shop/${c.slug}`} className="hover:text-pink-400 transition-colors" data-testid={`footer-cat-${c.slug}`}>{c.name}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-heading text-sm font-semibold text-white uppercase tracking-wider mb-4">Customer Care</h4>
                        <ul className="space-y-2 font-body text-sm">
                            <li><Link to="/login" className="hover:text-pink-400 transition-colors">My Account</Link></li>
                            <li><Link to="/cart" className="hover:text-pink-400 transition-colors">Cart</Link></li>
                            <li><Link to="/terms-and-conditions" className="hover:text-pink-400 transition-colors">Terms & Conditions</Link></li>
                            <li><Link to="/privacy-policy" className="hover:text-pink-400 transition-colors">Privacy Policy</Link></li>
                            <li><Link to="/return-policy" className="hover:text-pink-400 transition-colors">Return Policy</Link></li>
                        </ul>
                        <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                            <p className="text-xs font-body text-gray-400 mb-1 font-semibold uppercase tracking-wider">Return Policy</p>
                            <p className="text-[11px] font-body text-gray-500 leading-relaxed" data-testid="footer-return-summary">{cfg.returnSummary}</p>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-heading text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact</h4>
                        <ul className="space-y-3 font-body text-sm">
                            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-pink-500 flex-shrink-0" /><span data-testid="footer-address">{cfg.address}</span></li>
                            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-pink-500" /><a href={`tel:${cfg.phone}`} className="hover:text-pink-400" data-testid="footer-phone">{cfg.phone}</a></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <p className="text-xs font-body text-gray-500" data-testid="footer-copyright">
                        &copy; Copyright {year} BeautyByChancy. All rights reserved.
                    </p>
                    <div className="flex items-center gap-4 text-xs font-body text-gray-500">
                        <span data-testid="footer-ships-from">Ships from {cfg.city}</span>
                        <span>&middot;</span>
                        <span>Fast delivery nationwide</span>
                    </div>
                </div>
            </div>

            {/* ───── Mobile compact footer with visible copyright (always rendered) ───── */}
            <div className="md:hidden bg-gray-900 border-t border-gray-800 px-4 py-5" data-testid="mobile-footer">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-base font-bold text-white">Beauty<span className="text-pink-500">By</span>Chancy</h3>
                    <a href={`tel:${cfg.phone}`} className="flex items-center gap-1.5 text-xs font-body text-pink-400" data-testid="mobile-footer-phone">
                        <Phone className="w-3 h-3" /> {cfg.phone}
                    </a>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] font-body text-gray-400 mb-3">
                    <Link to="/shop" className="hover:text-pink-400 transition-colors">Shop</Link>
                    <Link to="/terms-and-conditions" className="hover:text-pink-400 transition-colors">Terms</Link>
                    <Link to="/privacy-policy" className="hover:text-pink-400 transition-colors">Privacy</Link>
                    <Link to="/return-policy" className="hover:text-pink-400 transition-colors">Returns</Link>
                </div>
                <p className="text-[11px] font-body text-gray-500 leading-relaxed" data-testid="footer-copyright-mobile">
                    &copy; Copyright {year} BeautyByChancy. All rights reserved. · {cfg.address}.
                </p>
            </div>
        </footer>
    );
}
